import { store } from '@/app/store';
import { updateStarredMessage } from '@/features/chats/model';
import { updateMessage } from '@/features/chats/model/conversation/reducer';
import { selectInvitePermissions } from '@/features/invite/model/selectors';
import { i18n } from '@/localization/i18n';
import { debounce, getUserIdFromJid, logger } from '@/shared/utils';
import { showErrorToast } from '@/shared/utils/toastMessage';
import { clearDraft } from '../model/draft';
import { updateRecentChatMessage, upsertRecentChatItem } from '../model/recentChat';
import { dispatchMessagesToConversation } from '../services/messageDispatchService';
import { addToMessageQueue } from '../services/messageQueueManager';
import { scrollRef } from '../ui/Conversation/conversationRefs';
import { formatConversationMessage, formatRecentChat } from './messageFormatter';

const debouncedScrollToBottom = debounce(scrollRef => {
   scrollRef?.current?.jumpToBottom({ smooth: true });
}, 100);

const checkInviteLimit = (chatId, isEditing) => {
   const { dispatch, getState } = store;
   const { inviteStatus, isReceiver, remainingMessages } = selectInvitePermissions(getState(), chatId);
   if (inviteStatus !== 1) return { hasPermission: true };
   if (!isReceiver && remainingMessages <= 0) {
      // Editing an already-sent message is always allowed, regardless of the
      // remaining new-message count — only warn when actually blocking a new send.
      if (!isEditing) {
         showErrorToast(i18n.t('invite.MESSAGE_LIMIT_REACHED'), { toastId: 'messageLimit' });
         dispatch(clearDraft(chatId));
      }
      return { hasPermission: false };
   }
   return { hasPermission: true, remainingMessages: !isReceiver ? remainingMessages : undefined };
};

/**
 * @param {Object} params
 * @param {string} params.activeChatJid
 */
export const handleSendMessage = ({ activeChatJid }) => {
   try {
      const activeChatId = getUserIdFromJid(activeChatJid);

      let formattedMessages = formatConversationMessage({ activeChatJid });

      const isEditing = formattedMessages?.[0]?.editMessageId !== formattedMessages?.[0]?.msgId;
      const { hasPermission, remainingMessages } = checkInviteLimit(activeChatId, isEditing);
      if (!isEditing && !hasPermission) return;
      const isLimitActive = !isEditing && hasPermission && typeof remainingMessages === 'number';
      if (isLimitActive) {
         formattedMessages = formattedMessages.slice(0, remainingMessages);
         const newRemaining = remainingMessages - formattedMessages.length;
         if (newRemaining === 0) {
            showErrorToast(i18n.t('invite.MESSAGE_LIMIT_REACHED'), { toastId: 'messageLimit' });
         }
      }
      store.dispatch(clearDraft(activeChatId));
      if (formattedMessages?.[0]?.editMessageId !== formattedMessages?.[0]?.msgId) {
         const { msgId, editMessageId, msgBody, editedStatus, deliveryAndReadInfo } = formattedMessages[0];
         store.dispatch(
            updateMessage({
               chatId: activeChatId,
               msgId,
               msgBody,
               msgStatus: 3,
               editMessageId,
               editedStatus,
               deliveryAndReadInfo,
            }),
         );
         store.dispatch(updateStarredMessage({ msgId, msgBody, msgStatus: 3, editMessageId, editedStatus }));
         store.dispatch(updateRecentChatMessage({ chatId: activeChatId, msgId, msgBody, editMessageId, editedStatus }));
         addToMessageQueue(formattedMessages, activeChatJid);
         return;
      }

      if (formattedMessages?.length > 0) {
         const latestMessage = formattedMessages[0];
         const uploadQueue = [...formattedMessages].reverse();

         store.dispatch(upsertRecentChatItem(formatRecentChat(latestMessage, { changeArchiveStatus: true })));

         // Dispatch to Redux to add the message to conversation via common service
         dispatchMessagesToConversation({
            chatId: activeChatId,
            messages: formattedMessages,
            resetUnreadlabel: true,
            immediate: true,
         });

         addToMessageQueue(uploadQueue, activeChatJid);
         debouncedScrollToBottom(scrollRef);
      }
   } catch (err) {
      logger.log('handleSendMessage', err);
   }
};
